
import React, { useRef, useEffect } from 'react';
import { ChatMessage } from '@/types/group';
import { User } from '@supabase/supabase-js';
import { formatDistanceToNow } from 'date-fns';
import { User as UserIcon } from 'lucide-react';
import { formatTimeTo12Hour } from '@/utils/dateUtils';

interface MessageListProps {
  messages: ChatMessage[];
  user: User | null;
  isLoading: boolean;
}

const MessageList: React.FC<MessageListProps> = ({ messages, user, isLoading }) => {
  const messageEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Format timestamp to show time in 12-hour format
  const formatMessageTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const timeString = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
    return formatTimeTo12Hour(timeString);
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-gray-500">Loading messages...</p>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center text-gray-500">
          <UserIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
          <p>No messages yet</p>
          <p className="text-sm">Be the first to send a message!</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {messages.map((msg) => (
        <div key={msg.id} className="flex flex-col">
          <div 
            className={`flex items-start gap-2 ${
              msg.user_id === user?.id ? 'justify-end' : ''
            }`}
          >
            <div
              className={`max-w-[80%] p-3 rounded-lg shadow-sm ${
                msg.user_id === user?.id
                  ? 'bg-navteal text-white rounded-tr-none'
                  : 'bg-gray-100 rounded-tl-none'
              }`}
            >
              <div className="flex flex-col">
                <p className={`text-xs font-medium mb-1 ${
                  msg.user_id === user?.id ? 'text-blue-100' : 'text-gray-500'
                }`}>
                  {msg.user_id === user?.id ? 'You' : msg.user_name}
                </p>
                <p className="break-words">{msg.message}</p>
              </div>
            </div>
          </div>
          <span className={`text-xs text-gray-400 mt-1 ${
            msg.user_id === user?.id ? 'text-right' : 'text-left'
          }`}>
            {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
          </span>
        </div>
      ))}
      <div ref={messageEndRef} />
    </>
  );
};

export default MessageList;
